import React, { useState, useEffect } from 'react';
import { Shield, Trophy, Globe, Edit, Save, Info } from 'lucide-react';

// Current Premier League teams and their positions as of May 4, 2025
const initialTeams = [
  { position: 1, name: "Liverpool", qualified: "UCL" },
  { position: 2, name: "Arsenal", qualified: null },
  { position: 3, name: "Manchester City", qualified: null },
  { position: 4, name: "Newcastle United", qualified: null },
  { position: 5, name: "Chelsea", qualified: null },
  { position: 6, name: "Nottingham Forest", qualified: null },
  { position: 7, name: "Aston Villa", qualified: null },
  { position: 8, name: "Bournemouth", qualified: null },
  { position: 9, name: "Fulham", qualified: null },
  { position: 10, name: "Brighton & Hove Albion", qualified: null },
  { position: 11, name: "Brentford", qualified: null },
  { position: 12, name: "Crystal Palace", qualified: null },
  { position: 13, name: "Everton", qualified: null },
  { position: 14, name: "Wolverhampton Wanderers", qualified: null },
  { position: 15, name: "Manchester United", qualified: null },
  { position: 16, name: "Tottenham Hotspur", qualified: null },
  { position: 17, name: "West Ham United", qualified: null },
  { position: 18, name: "Ipswich Town", qualified: null },
  { position: 19, name: "Leicester City", qualified: null },
  { position: 20, name: "Southampton", qualified: null }
];

// Cup competitions
const competitions = [
  { id: "facup", name: "FA Cup", type: "domestic", qualificationSpot: "UEL" },
  { id: "eflcup", name: "EFL Cup", type: "domestic", qualificationSpot: "UECL" },
  { id: "ucl", name: "UEFA Champions League", type: "european", qualificationSpot: "UCL" },
  { id: "uel", name: "UEFA Europa League", type: "european", qualificationSpot: "UCL" },
  { id: "uecl", name: "UEFA Conference League", type: "european", qualificationSpot: "UEL" }
];

const SimplifiedPremierLeagueApp = () => {
  const [teams, setTeams] = useState(initialTeams);
  const [cupWinners, setCupWinners] = useState({
    facup: "",
    eflcup: "",
    ucl: "",
    uel: "",
    uecl: ""
  });
  const [qualifiedTeams, setQualifiedTeams] = useState({
    ucl: [],
    uel: [],
    uecl: []
  });
  const [qualificationSources, setQualificationSources] = useState({});
  const [showRules, setShowRules] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("Initial load: " + new Date().toLocaleString());
  const [totalQualified, setTotalQualified] = useState(0);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  
  // Fetch latest Premier League standings from external source
  const fetchLatestLeagueData = () => {
    setLastUpdated("Fetching latest Premier League standings from Sporting Life...");
    
    // Current Premier League standings as of May 5, 2025 (GMT)
    const currentStandings = [
      { position: 1, name: "Liverpool", qualified: null },
      { position: 2, name: "Arsenal", qualified: null },
      { position: 3, name: "Manchester City", qualified: null },
      { position: 4, name: "Newcastle United", qualified: null },
      { position: 5, name: "Chelsea", qualified: null },
      { position: 6, name: "Nottingham Forest", qualified: null },
      { position: 7, name: "Aston Villa", qualified: null },
      { position: 8, name: "Bournemouth", qualified: null },
      { position: 9, name: "Brentford", qualified: null },
      { position: 10, name: "Brighton & Hove Albion", qualified: null },
      { position: 11, name: "Fulham", qualified: null },
      { position: 12, name: "Crystal Palace", qualified: null },
      { position: 13, name: "Wolverhampton Wanderers", qualified: null },
      { position: 14, name: "Everton", qualified: null },
      { position: 15, name: "Manchester United", qualified: null },
      { position: 16, name: "Tottenham Hotspur", qualified: null },
      { position: 17, name: "West Ham United", qualified: null },
      { position: 18, name: "Ipswich Town", qualified: null },
      { position: 19, name: "Leicester City", qualified: null },
      { position: 20, name: "Southampton", qualified: null }
    ];
    
    // In a real implementation, this would make an API call to fetch the current Premier League table
    // For demonstration, we'll use setTimeout to simulate an API call
    setTimeout(() => {
      // Update teams with the current standings
      setTeams(currentStandings);
      
      // Only show alert when manually refreshed, not on initial load
      if (lastUpdated !== "Initial load: " + new Date().toLocaleString()) {
        window.alert("Table updated with latest standings from Sporting Life as of May 5, 2025 (GMT)!");
      }
      
      setLastUpdated("Latest standings fetched from Sporting Life: " + new Date().toLocaleString());
    }, 1500);
  };
  
  // Call fetchLatestLeagueData when component mounts
  useEffect(() => {
    fetchLatestLeagueData();
  }, []);

  // Modify moveTeam function to work without points
  const moveTeam = (currentPos, direction) => {
    const newPos = direction === 'up' ? currentPos - 1 : currentPos + 1;
    
    if (newPos < 1 || newPos > teams.length) return;
    
    const updatedTeams = [...teams];
    const team1Index = updatedTeams.findIndex(t => t.position === currentPos);
    const team2Index = updatedTeams.findIndex(t => t.position === newPos);
    
    if (team1Index !== -1 && team2Index !== -1) {
      // Swap positions
      updatedTeams[team1Index] = { ...updatedTeams[team1Index], position: newPos };
      updatedTeams[team2Index] = { ...updatedTeams[team2Index], position: currentPos };
      
      // Sort by position
      updatedTeams.sort((a, b) => a.position - b.position);
      
      setTeams(updatedTeams);
      setLastUpdated("Teams updated: " + new Date().toLocaleString());
    }
  };

  // Handle cup winner selection
  const handleCupWinnerChange = (e) => {
    setCupWinners({
      ...cupWinners,
      [e.target.name]: e.target.value
    });
  };

  // Calculate European qualification
  const calculateQualification = () => {
    // Reset qualifications
    const resetTeams = teams.map(team => ({ ...team, qualified: null }));
    const sortedTeams = [...resetTeams].sort((a, b) => a.position - b.position);
    
    // Results to track
    const qualified = { ucl: [], uel: [], uecl: [] };
    const qualificationReasons = {};
    
    // Track qualification sources
    const sources = {};
    
    // 1. Handle Cup Winners first - they get priority
    // UCL Winner - automatic UCL qualification
    if (cupWinners.ucl) {
      const uclWinner = sortedTeams.find(t => t.name === cupWinners.ucl);
      if (uclWinner) {
        uclWinner.qualified = "UCL";
        qualified.ucl.push(uclWinner.name);
        sources[uclWinner.name] = "UCL Winner";
      }
    }
    
    // UEL Winner - automatic UCL qualification
    if (cupWinners.uel) {
      const uelWinner = sortedTeams.find(t => t.name === cupWinners.uel);
      if (uelWinner) {
        // Even if they're already qualified for UCL, we update the reason
        uelWinner.qualified = "UCL";
        if (!qualified.ucl.includes(uelWinner.name)) {
          qualified.ucl.push(uelWinner.name);
        }
        sources[uelWinner.name] = "UEL Winner";
      }
    }
    
    // UECL Winner - automatic UEL qualification, unless already in UCL
    if (cupWinners.uecl) {
      const ueclWinner = sortedTeams.find(t => t.name === cupWinners.uecl);
      if (ueclWinner) {
        if (ueclWinner.qualified === "UCL") {
          // Already in UCL, so leave them there
        } else {
          ueclWinner.qualified = "UEL";
          qualified.uel.push(ueclWinner.name);
          sources[ueclWinner.name] = "UECL Winner";
        }
      }
    }
    
    // FA Cup Winner - automatic UEL qualification, unless already in UCL
    if (cupWinners.facup) {
      const facupWinner = sortedTeams.find(t => t.name === cupWinners.facup);
      if (facupWinner) {
        if (facupWinner.qualified === "UCL") {
          // Already in UCL, so leave them there
          // Mark for possible pass-down of UEL spot
          qualificationReasons.faCupPassDown = true;
        } else {
          facupWinner.qualified = "UEL";
          if (!qualified.uel.includes(facupWinner.name)) {
            qualified.uel.push(facupWinner.name);
          }
          sources[facupWinner.name] = "FA Cup Winner";
        }
      }
    }
    
    // EFL Cup Winner - automatic UECL qualification, unless already in UCL or UEL
    if (cupWinners.eflcup) {
      const eflcupWinner = sortedTeams.find(t => t.name === cupWinners.eflcup);
      if (eflcupWinner) {
        if (eflcupWinner.qualified === "UCL" || eflcupWinner.qualified === "UEL" || eflcupWinner.position <= 6) {
          // Already in UCL or UEL (or positioned to qualify), so leave them there and mark for pass-down
          if (!eflcupWinner.qualified) {
            // If they're in position 6 but not yet qualified, qualify them for UEL
            eflcupWinner.qualified = "UEL";
            qualified.uel.push(eflcupWinner.name);
            sources[eflcupWinner.name] = "League Position (6)";
          }
          // Mark for pass-down regardless
          qualificationReasons.eflPassDown = true;
        } else {
          eflcupWinner.qualified = "UECL";
          qualified.uecl.push(eflcupWinner.name);
          sources[eflcupWinner.name] = "EFL Cup Winner";
        }
      }
    }
    
    // 2. Handle league positions
    // Top 5 get UCL (5th through EPS)
    for (let i = 0; i < 5; i++) {
      if (i < sortedTeams.length && !sortedTeams[i].qualified) {
        sortedTeams[i].qualified = "UCL";
        qualified.ucl.push(sortedTeams[i].name);
        sources[sortedTeams[i].name] = i === 4 ? "5th Place (EPS)" : `League Position (${i+1})`;
      }
    }
    
    // 6th gets UEL if not already qualified higher
    if (sortedTeams.length >= 6 && !sortedTeams[5].qualified) {
      sortedTeams[5].qualified = "UEL";
      qualified.uel.push(sortedTeams[5].name);
      sources[sortedTeams[5].name] = "League Position (6)";
    }
    
    // 7th gets UECL if not already qualified higher
    if (sortedTeams.length >= 7 && !sortedTeams[6].qualified) {
      sortedTeams[6].qualified = "UECL";
      qualified.uecl.push(sortedTeams[6].name);
      sources[sortedTeams[6].name] = "League Position (7)";
    }
    
    // Handle FA Cup pass-down if needed
    if (qualificationReasons.faCupPassDown) {
      // First check if 6th place already has UEL (might be taken by UECL winner)
      const sixthPlace = sortedTeams.find(t => t.position === 6);
      if (sixthPlace && sixthPlace.qualified !== "UEL" && sixthPlace.qualified !== "UCL") {
        sixthPlace.qualified = "UEL";
        qualified.uel.push(sixthPlace.name);
        sources[sixthPlace.name] = `FA Cup Pass Down (from ${cupWinners.facup})`;
      } else {
        // Find next eligible team if 6th already qualified
        let foundTeam = false;
        for (let i = 6; i < sortedTeams.length && !foundTeam; i++) {
          // Skip teams already qualified for any competition
          if (!sortedTeams[i].qualified) {
            sortedTeams[i].qualified = "UEL";
            qualified.uel.push(sortedTeams[i].name);
            sources[sortedTeams[i].name] = `FA Cup Pass Down (from ${cupWinners.facup})`;
            foundTeam = true;
          }
        }
      }
    }
    
    // Handle EFL Cup pass-down if needed
    if (qualificationReasons.eflPassDown) {
      // First check if 7th place already has UECL
      const seventhPlace = sortedTeams.find(t => t.position === 7);
      if (seventhPlace && !seventhPlace.qualified) {
        seventhPlace.qualified = "UECL";
        qualified.uecl.push(seventhPlace.name);
        sources[seventhPlace.name] = `EFL Cup Pass Down (from ${cupWinners.eflcup})`;
      } else {
        // Find next eligible team if 7th already qualified
        let foundTeam = false;
        for (let i = 7; i < sortedTeams.length && !foundTeam; i++) {
          if (!sortedTeams[i].qualified) {
            sortedTeams[i].qualified = "UECL";
            qualified.uecl.push(sortedTeams[i].name);
            sources[sortedTeams[i].name] = `EFL Cup Pass Down (from ${cupWinners.eflcup})`;
            foundTeam = true;
          }
        }
      }
    }
    
    // Final check - a team can't be in a lower competition if it qualifies for a higher one
    for (let i = 0; i < sortedTeams.length; i++) {
      if (sortedTeams[i].qualified && sortedTeams[i].position <= 5) {
        // If a team in the top 5 isn't in UCL, upgrade them
        if (sortedTeams[i].qualified !== "UCL") {
          // Move them from their current competition to UCL
          if (sortedTeams[i].qualified === "UEL") {
            qualified.uel = qualified.uel.filter(name => name !== sortedTeams[i].name);
          } else if (sortedTeams[i].qualified === "UECL") {
            qualified.uecl = qualified.uecl.filter(name => name !== sortedTeams[i].name);
          }
          
          sortedTeams[i].qualified = "UCL";
          if (!qualified.ucl.includes(sortedTeams[i].name)) {
            qualified.ucl.push(sortedTeams[i].name);
            sources[sortedTeams[i].name] = "League Position (top 5)";
          }
        }
      }
    }
    
    // Count per-competition totals
    const uclTotal = qualified.ucl.length;
    const uelTotal = qualified.uel.length;
    const ueclTotal = qualified.uecl.length;
    
    // Update state
    setTeams(sortedTeams);
    setQualifiedTeams(qualified);
    setQualificationSources(sources);
    setTotalQualified(uclTotal + uelTotal + ueclTotal);
    setLastUpdated("Qualification calculated: " + new Date().toLocaleString());
    
    // Show alert if we've reached 7 UCL teams
    if (uclTotal >= 7) {
      alert("Maximum of 7 Champions League teams reached!");
    }
  };
  
  // Handle email submission
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      alert("Please enter a valid email address");
      return;
    }
    
    // In a real implementation, this would send an API request to a backend service
    // For demo purposes, we'll just show an alert
    
    // Prepare the data for the email
    const emailData = {
      email: email,
      date: new Date().toLocaleString(),
      leagueTable: teams.map(team => ({
        position: team.position,
        name: team.name,
        qualified: team.qualified
      })),
      cupWinners: cupWinners,
      qualifiedTeams: qualifiedTeams,
      qualificationSources: qualificationSources
    };
    
    // For demo purposes, show what would be sent
    console.log("Email data to send:", emailData);
    
    // Show success message
    setEmailSent(true);
    
    // Reset after 5 seconds
    setTimeout(() => {
      setEmailSent(false);
    }, 5000);
    
    alert("Your prediction has been sent to " + email + ". You can now forward this to your bookmaker for odds!");
  };
  
  // Format prediction summary for sharing
  const getPredictionSummary = () => {
    let summary = "Premier League UEFA Qualification Prediction\n\n";
    
    summary += "Champions League:\n";
    qualifiedTeams.ucl.forEach(team => {
      summary += `- ${team} (via ${qualificationSources[team]})\n`;
    });
    
    summary += "\nEuropa League:\n";
    qualifiedTeams.uel.forEach(team => {
      summary += `- ${team} (via ${qualificationSources[team]})\n`;
    });
    
    summary += "\nConference League:\n";
    qualifiedTeams.uecl.forEach(team => {
      summary += `- ${team} (via ${qualificationSources[team]})\n`;
    });
    
    return summary;
  };

  // Sort teams by position
  const sortedTeams = [...teams].sort((a, b) => a.position - b.position);

  return (
    <div className="bg-gradient-to-b from-green-800 to-green-900 min-h-screen text-white p-4 font-sans">
      <div className="container mx-auto">
        {/* Featured Betting Partner - Bet365 */}
        <div className="bg-black bg-opacity-70 rounded-lg p-4 mb-6 border-t-4 border-yellow-400">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Trophy className="mr-3 text-yellow-400" size={28} />
              <h2 className="text-2xl font-bold">Featured Partner: Bet365</h2>
            </div>
            <a 
              href="https://www.bet365.com/?affiliate=YOUR_AFFILIATE_ID" 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-yellow-500 hover:bg-yellow-400 text-black py-2 px-4 rounded font-bold"
            >
              Great odds on Premier League
            </a>
          </div>
        </div>
        
        {/* Header */}
        <div className="bg-black bg-opacity-50 rounded-lg p-6 mb-8 border-t-4 border-green-500">
          <div className="flex items-center mb-4">
            <Shield className="mr-3 text-yellow-400" size={32} />
            <h1 className="text-3xl font-bold">Premier League UEFA Qualification 2025-2026</h1>
          </div>
          <p className="text-gray-300">
            Configure the Premier League standings and cup winners to see European qualification scenarios.
          </p>
          <p className="mt-1 text-yellow-300 font-semibold">
            The Premier League has earned an extra Champions League place (5th position) through the European Performance Spots system.
          </p>
          <p className="text-sm text-gray-400 mt-2">Last updated: {lastUpdated}</p>
          
          <button 
            className="mt-2 flex items-center text-yellow-300 hover:text-yellow-100"
            onClick={() => setShowRules(!showRules)}
          >
            <Info size={18} className="mr-1" />
            {showRules ? 'Hide qualification rules' : 'Show qualification rules'}
          </button>
          
          {showRules && (
            <div className="mt-3 bg-black bg-opacity-50 p-4 rounded-md text-sm">
              <h3 className="font-bold text-yellow-300 mb-2">UEFA Qualification Rules for 2025-2026:</h3>
              <ul className="list-disc pl-5 space-y-1 text-gray-200">
                <li>Top 5 in Premier League qualify for Champions League (5th place through European Performance Spots)</li>
                <li>6th place qualifies for Europa League</li>
                <li>7th place qualifies for Conference League</li>
                <li>FA Cup winner qualifies for Europa League</li>
                <li>EFL Cup winner qualifies for Conference League</li>
                <li>UEFA Champions League winner qualifies for next season's Champions League</li>
                <li>UEFA Europa League winner qualifies for next season's Champions League</li>
                <li>UEFA Conference League winner qualifies for next season's Europa League</li>
                <li>Up to 10 Premier League teams can qualify for European competitions</li>
                <li>Maximum of 7 teams can qualify for Champions League</li>
                <li>Teams qualifying for higher competitions (e.g., UCL) through league position will never be placed in lower competitions</li>
                <li>If a cup winner already qualifies for a higher competition through league position, they remain in the higher competition</li>
              </ul>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Premier League Table */}
          <div className="lg:col-span-2">
            <div className="bg-black bg-opacity-70 rounded-lg p-4 border-t-4 border-purple-500">
              <div className="flex items-center mb-4">
                <Trophy className="mr-2 text-purple-400" size={24} />
                <h2 className="text-2xl font-bold">Premier League Table</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-purple-900 text-white">
                      <th className="p-2 text-left">Pos</th>
                      <th className="p-2 text-left">Club</th>
                      <th className="p-2 text-center">Qualification</th>
                      <th className="p-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTeams.map((team) => (
                      <tr key={team.position} className={`
                        border-b border-gray-800 hover:bg-black hover:bg-opacity-40
                        ${team.qualified === 'UCL' ? 'bg-blue-900 bg-opacity-40' : ''}
                        ${team.qualified === 'UEL' ? 'bg-orange-900 bg-opacity-40' : ''}
                        ${team.qualified === 'UECL' ? 'bg-green-900 bg-opacity-40' : ''}
                        ${team.position === 5 ? 'bg-blue-900 bg-opacity-20' : ''}
                        ${team.position > 17 ? 'bg-red-900 bg-opacity-30' : ''}`}
                      >
                        <td className="p-2">
                          <div className="flex items-center">
                            <span className="font-bold mr-2">{team.position}</span>
                            <div className="flex flex-col">
                              {team.position > 1 && (
                                <button 
                                  onClick={() => moveTeam(team.position, 'up')}
                                  className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs font-bold mb-1"
                                  title="Move up"
                                >
                                  ↑
                                </button>
                              )}
                              {team.position < 20 && (
                                <button 
                                  onClick={() => moveTeam(team.position, 'down')}
                                  className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs font-bold"
                                  title="Move down"
                                >
                                  ↓
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-2 font-semibold">{team.name}</td>
                        <td className="p-2 text-center">
                          {team.qualified === 'UCL' && <span className="bg-blue-700 px-2 py-1 rounded text-xs">UCL</span>}
                          {team.qualified === 'UEL' && <span className="bg-orange-600 px-2 py-1 rounded text-xs">UEL</span>}
                          {team.qualified === 'UECL' && <span className="bg-green-600 px-2 py-1 rounded text-xs">UECL</span>}
                        </td>
                        <td className="p-2 text-center">
                          <div className="flex justify-center">
                            <button
                              onClick={() => {
                                const newTeams = teams.map(t => 
                                  t.position === team.position 
                                    ? { ...t, qualified: null } 
                                    : t
                                );
                                setTeams(newTeams);
                              }}
                              className="bg-red-600 p-1 rounded hover:bg-red-500 text-xs"
                              title="Clear qualification"
                            >
                              Clear
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-3 text-xs flex justify-between text-gray-300">
                <div><span className="inline-block w-3 h-3 bg-blue-900 mr-1"></span> Champions League</div>
                <div><span className="inline-block w-3 h-3 bg-orange-700 mr-1"></span> Europa League</div>
                <div><span className="inline-block w-3 h-3 bg-green-700 mr-1"></span> Conference League</div>
                <div><span className="inline-block w-3 h-3 bg-red-800 mr-1"></span> Relegation</div>
              </div>
              
              <div className="mt-4">
                <button
                  onClick={() => {
                    // Use the same function to fetch latest data
                    fetchLatestLeagueData();
                  }}
                  className="bg-purple-600 hover:bg-purple-500 text-white py-2 px-4 rounded font-bold flex items-center justify-center"
                >
                  <Trophy className="mr-2" size={18} />
                  Update Table
                </button>
              </div>
            </div>
          </div>
          
          {/* Cup Winners and Qualification */}
          <div className="lg:col-span-1">
            {/* Cup Winners Panel */}
            <div className="bg-black bg-opacity-70 rounded-lg p-4 border-t-4 border-yellow-500 mb-6">
              <div className="flex items-center mb-4">
                <Trophy className="mr-2 text-yellow-400" size={24} />
                <h2 className="text-2xl font-bold">Cup Winners</h2>
              </div>
              
              <div className="space-y-4">
                {competitions.map(comp => (
                  <div key={comp.id} className="border-b border-gray-700 pb-3">
                    <label className="font-semibold block mb-1">
                      {comp.name} Winner:
                      <span className="text-xs ml-2 text-gray-400">
                        ({comp.qualificationSpot === "UCL" ? "Champions League spot" : 
                          comp.qualificationSpot === "UEL" ? "Europa League spot" : 
                          "Conference League spot"})
                      </span>
                    </label>
                    <select 
                      name={comp.id}
                      value={cupWinners[comp.id]}
                      onChange={handleCupWinnerChange}
                      className="w-full bg-gray-800 text-white p-2 rounded border border-gray-700"
                    >
                      <option value="">-- Select Winner --</option>
                      {sortedTeams.map(team => (
                        <option key={`${comp.id}-${team.name}`} value={team.name}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={calculateQualification}
                className="w-full mt-4 bg-yellow-600 hover:bg-yellow-500 text-white py-2 px-4 rounded font-bold flex items-center justify-center mb-4"
              >
                <Trophy className="mr-2" size={20} />
                Calculate UEFA Qualification
              </button>
              
              <button 
                onClick={fetchLatestLeagueData}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white py-2 px-4 rounded font-bold flex items-center justify-center"
              >
                <Globe className="mr-2" size={20} />
                Refresh Premier League Standings
              </button>
            </div>
            
            {/* Qualification Results */}
            <div className="bg-black bg-opacity-70 rounded-lg p-4 border-t-4 border-blue-500">
              <div className="flex items-center mb-4 justify-between">
                <div className="flex items-center">
                  <Globe className="mr-2 text-blue-400" size={24} />
                  <h2 className="text-2xl font-bold">Qualification Results</h2>
                </div>
                {totalQualified > 0 && (
                  <div className="bg-blue-700 px-2 py-1 rounded text-xs">
                    {totalQualified} teams qualified
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="border-b border-gray-700 pb-3">
                  <h3 className="font-bold text-blue-300 mb-2">Champions League</h3>
                  {qualifiedTeams.ucl.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {qualifiedTeams.ucl.map(team => (
                        <li key={`ucl-${team}`} className="mb-1">
                          {team}
                          {qualificationSources[team] && (
                            <span className="text-xs text-gray-400 ml-2">
                              via {qualificationSources[team]}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400 italic">No teams qualified yet</p>
                  )}
                </div>
                
                <div className="border-b border-gray-700 pb-3">
                  <h3 className="font-bold text-orange-300 mb-2">Europa League</h3>
                  {qualifiedTeams.uel.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {qualifiedTeams.uel.map(team => (
                        <li key={`uel-${team}`} className="mb-1">
                          {team}
                          {qualificationSources[team] && (
                            <span className="text-xs text-gray-400 ml-2">
                              via {qualificationSources[team]}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400 italic">No teams qualified yet</p>
                  )}
                </div>
                
                <div>
                  <h3 className="font-bold text-green-300 mb-2">Conference League</h3>
                  {qualifiedTeams.uecl.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {qualifiedTeams.uecl.map(team => (
                        <li key={`uecl-${team}`} className="mb-1">
                          {team}
                          {qualificationSources[team] && (
                            <span className="text-xs text-gray-400 ml-2">
                              via {qualificationSources[team]}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400 italic">No teams qualified yet</p>
                  )}
                </div>
                
                {totalQualified >= 8 && (
                  <div className="mt-4 pt-3 border-t border-gray-700">
                    <p className={`text-sm ${totalQualified > 9 ? 'text-red-400' : totalQualified > 8 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {totalQualified === 10 ? (
                        <strong>Maximum of 10 English teams in European competitions reached!</strong>
                      ) : (
                        <span>Currently {totalQualified} English teams would qualify for European competitions</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Distribution: {qualifiedTeams.ucl.length} in Champions League, 
                      {qualifiedTeams.uel.length} in Europa League, 
                      {qualifiedTeams.uecl.length} in Conference League
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Email Submission Section */}
        <div className="mt-8 bg-black bg-opacity-70 rounded-lg p-6 border-t-4 border-red-500">
          <div className="flex items-center mb-4">
            <Trophy className="mr-3 text-red-400" size={28} />
            <h2 className="text-2xl font-bold">Save Your Prediction & Get Betting Odds</h2>
          </div>
          
          <div className="max-w-md">
            <p className="text-gray-300 mb-4">
              Save your qualification prediction and get odds from our betting partners. Enter your email address below to receive your prediction details.
            </p>
            
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-800 text-white p-3 rounded border border-gray-700"
                  placeholder="your.email@example.com"
                  required
                />
              </div>
              
              <button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-500 text-white py-3 px-4 rounded font-bold flex items-center justify-center"
                disabled={emailSent}
              >
                {emailSent ? (
                  <span>Prediction Sent! ✓</span>
                ) : (
                  <>
                    <Trophy className="mr-2" size={20} />
                    Send My Prediction
                  </>
                )}
              </button>
              
              <p className="text-xs text-gray-400">
                By submitting, you agree to receive your prediction details and occasional betting offers from our partners. You can unsubscribe at any time.
              </p>
            </form>
          </div>
        </div>
        
        {/* Our Betting Partners */}
        <div className="mt-8 bg-black bg-opacity-70 rounded-lg p-4 border-t-4 border-purple-500">
          <div className="flex items-center mb-4">
            <Globe className="mr-2 text-purple-400" size={24} />
            <h2 className="text-2xl font-bold">Our Betting Partners</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-800 bg-opacity-50 p-4 rounded hover:bg-gray-700 transition-colors">
              <a href="https://www.williamhill.com/?aff=YOUR_AFFILIATE_ID" target="_blank" rel="noopener noreferrer" className="flex items-center text-white">
                <div className="w-12 h-12 bg-yellow-600 rounded flex items-center justify-center mr-3">
                  <Trophy size={24} />
                </div>
                <div>
                  <p className="font-bold">William Hill</p>
                  <p className="text-sm text-gray-400">Exclusive European football markets</p>
                </div>
              </a>
            </div>
            
            <div className="bg-gray-800 bg-opacity-50 p-4 rounded hover:bg-gray-700 transition-colors">
              <a href="https://www.paddypower.com/?pid=YOUR_AFFILIATE_ID" target="_blank" rel="noopener noreferrer" className="flex items-center text-white">
                <div className="w-12 h-12 bg-green-600 rounded flex items-center justify-center mr-3">
                  <Shield size={24} />
                </div>
                <div>
                  <p className="font-bold">Paddy Power</p>
                  <p className="text-sm text-gray-400">Competitive accumulator offers</p>
                </div>
              </a>
            </div>
            
            <div className="bg-gray-800 bg-opacity-50 p-4 rounded hover:bg-gray-700 transition-colors">
              <a href="https://www.skybet.com/?aff=YOUR_AFFILIATE_ID" target="_blank" rel="noopener noreferrer" className="flex items-center text-white">
                <div className="w-12 h-12 bg-blue-600 rounded flex items-center justify-center mr-3">
                  <Globe size={24} />
                </div>
                <div>
                  <p className="font-bold">Sky Bet</p>
                  <p className="text-sm text-gray-400">Special UCL qualification bets</p>
                </div>
              </a>
            </div>
            
            <div className="bg-gray-800 bg-opacity-50 p-4 rounded hover:bg-gray-700 transition-colors">
              <a href="https://www.ladbrokes.com/?a=YOUR_AFFILIATE_ID" target="_blank" rel="noopener noreferrer" className="flex items-center text-white">
                <div className="w-12 h-12 bg-red-600 rounded flex items-center justify-center mr-3">
                  <Trophy size={24} />
                </div>
                <div>
                  <p className="font-bold">Ladbrokes</p>
                  <p className="text-sm text-gray-400">Top European football odds</p>
                </div>
              </a>
            </div>
          </div>
          
          <div className="mt-4 text-xs text-gray-400 bg-black bg-opacity-40 p-3 rounded">
            <p className="text-center"><strong>Please gamble responsibly. When the fun stops, stop.</strong></p>
            <p className="text-center mt-1">18+ only. Terms and conditions apply to all offers.</p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="mt-8 text-center text-gray-400 text-sm">
          <p>Data based on Premier League standings as of May 4, 2025</p>
          <p className="mt-1">UEFA qualification rules applied for the 2025-2026 season</p>
          <p className="mt-1">Data sources: <a href="https://www.premierleague.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Premier League</a>, <a href="https://www.uefa.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">UEFA</a></p>
          <p className="mt-2">Betting partners shown are for demonstration purposes only. This tool is not affiliated with any betting company or the Premier League.</p>
        </div>
      </div>
    </div>
  );
};

export default SimplifiedPremierLeagueApp;